import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCheck, Undo2, BadgeCheck, ShieldQuestion, ShieldCheck, MessageSquareText, Mail, MousePointerClick, Sparkles } from "lucide-react";
import { ROLE_LABELS } from "../ctx";
import { copilotAskUrl, feedbackReplyPrompt } from "@/lib/copilotLink";
import { catOf, sourceOf, fmtFull, fmtPhone, senderName } from "./utils";

function Pill({ cls, children, testid }) {
  return <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${cls}`}>{children}</span>;
}

const REPEATS_SHOWN = 8;
function Repeats({ items }) {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, REPEATS_SHOWN);
  const first = items[items.length - 1], last = items[0];
  return (
    <div data-testid="feedback-detail-repeats">
      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink/45 mb-1.5">
        Said {items.length} times · {fmtFull(first.created_at)} → {fmtFull(last.created_at)}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {shown.map((i) => <span key={i.id} className="rounded-full bg-sand-100 px-2.5 py-1 text-xs text-ink/70 tabular-nums">{fmtFull(i.created_at)}</span>)}
        {items.length > REPEATS_SHOWN && (
          <button data-testid="feedback-repeats-toggle" onClick={() => setAll(!all)} className="rounded-full border border-sand-200 px-2.5 py-1 text-xs font-bold text-ink/60 hover:border-ink/40 transition-colors">
            {all ? "Show fewer" : `+${items.length - REPEATS_SHOWN} more`}
          </button>
        )}
      </div>
    </div>
  );
}

function Sender({ f }) {
  const role = (f.role || "").toLowerCase();
  const isResident = role === "resident";
  const staffLabel = role && role !== "unregistered" && !isResident ? (ROLE_LABELS[role] || "Team") : null;
  return (
    <div className="rounded-2xl border border-sand-200 bg-sand-50/60 p-4" data-testid="feedback-detail-sender">
      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink/45 mb-2">Who said it</div>
      <div className="flex items-center gap-2 flex-wrap text-sm">
        <span className="font-semibold text-ink">{senderName(f)}</span>
        {isResident
          ? <Pill testid={`feedback-role-${f.id}`} cls="bg-emerald-50 text-emerald-700"><BadgeCheck className="h-3 w-3" /> Registered</Pill>
          : staffLabel
            ? <Pill testid={`feedback-role-${f.id}`} cls="bg-ink text-white"><ShieldCheck className="h-3 w-3" /> {staffLabel}</Pill>
            : <Pill testid={`feedback-role-${f.id}`} cls="bg-amber-50 text-amber-700"><ShieldQuestion className="h-3 w-3" /> Unregistered</Pill>}
      </div>
      {(f.phone || f.email || f.page) && (
        <div className="flex items-center gap-4 mt-2.5 flex-wrap text-sm">
          {f.phone && <a data-testid="feedback-detail-phone" href={`sms:${f.phone}`} className="inline-flex items-center gap-1.5 text-ink/70 hover:text-redline-600 transition-colors"><MessageSquareText className="h-3.5 w-3.5" /> Text {fmtPhone(f.phone)}</a>}
          {f.email && <a data-testid="feedback-detail-email" href={`mailto:${f.email}`} className="inline-flex items-center gap-1.5 text-ink/70 hover:text-redline-600 transition-colors"><Mail className="h-3.5 w-3.5" /> {f.email}</a>}
          {f.page && <span className="text-ink/45">from {f.page}</span>}
        </div>
      )}
    </div>
  );
}

export function FeedbackDetail({ group, onStatus, busy, isPlatformView, onBack, mobile, copilotOn }) {
  const navigate = useNavigate();
  const back = (
    <button data-testid="feedback-back-mobile" onClick={onBack}
      className="lg:hidden flex items-center gap-1.5 text-sm font-semibold text-ink px-4 py-3 border-b border-sand-200 shrink-0 bg-sand-50">
      <ArrowLeft className="h-4 w-4" /> All feedback
    </button>
  );
  if (!group) {
    return (
      <div className="h-full flex flex-col" data-testid="feedback-detail-empty">
        {mobile && back}
        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-muted-foreground text-sm p-10 text-center">
          <MousePointerClick className="h-7 w-7 text-sand-300" />
          Pick something on the left to read it in full.
          <span className="text-xs text-ink/40">Tip: ↑ ↓ to move, E to mark done</span>
        </div>
      </div>
    );
  }
  const f = group.first;
  const cat = catOf(f);
  const src = sourceOf(f);
  const isDone = f.status !== "new";
  const n = group.items.length;
  return (
    <div className="flex flex-col h-full" data-testid="feedback-detail">
      {mobile && back}
      <div className="px-6 pt-5 pb-4 border-b border-sand-100 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <Pill cls={cat.cls}><cat.icon className="h-3.5 w-3.5" /> {cat.label}</Pill>
          <Pill cls="bg-sand-100 text-ink/70"><src.icon className="h-3.5 w-3.5" /> {src.label}</Pill>
          {isPlatformView && f.community && <span className="font-semibold text-ink/60">{f.community}</span>}
          <span className="text-ink/45" data-testid="feedback-detail-time">{fmtFull(f.created_at)}</span>
          {f.status === "archived" && <span className="rounded-full bg-sand-100 px-2 py-0.5 text-ink/50">Archived</span>}
        </div>
        {isDone ? (
          <button data-testid={`feedback-reopen-${f.id}`} onClick={() => onStatus(group, "new")} disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full border border-sand-300 bg-white px-4 py-2 text-sm font-bold text-ink/75 hover:border-ink/40 transition-colors disabled:opacity-50">
            <Undo2 className="h-4 w-4" /> Move to Inbox{n > 1 ? ` (${n})` : ""}
          </button>
        ) : (
          <div className="flex items-center gap-2">
            {copilotOn && (
              <button data-testid={`feedback-draft-reply-${f.id}`} onClick={() => navigate(copilotAskUrl(feedbackReplyPrompt(f)))}
                className="inline-flex items-center gap-1.5 rounded-full border border-sand-300 bg-white px-4 py-2 text-sm font-bold text-ink/75 hover:border-ink/40 transition-colors">
                <Sparkles className="h-4 w-4 text-redline-500" /> Draft a reply
              </button>
            )}
            <button data-testid={`feedback-done-${f.id}`} onClick={() => onStatus(group, "reviewed")} disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700 transition-colors disabled:opacity-50">
              <CheckCheck className="h-4 w-4" /> Done{n > 1 ? ` · all ${n}` : ""}
            </button>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto chat-scroll px-6 py-5 space-y-5">
        <p data-testid="feedback-detail-message" className="text-base sm:text-lg text-ink leading-relaxed whitespace-pre-wrap">{f.message}</p>
        {f.verbatim && f.verbatim !== f.message && (
          <blockquote data-testid="feedback-detail-verbatim" className="border-l-2 border-sand-300 pl-4 text-sm italic text-ink/60 whitespace-pre-wrap">
            &ldquo;{f.verbatim}&rdquo;
          </blockquote>
        )}
        {n > 1 && <Repeats items={group.items} />}
        <Sender f={f} />
      </div>
    </div>
  );
}
